<template>
  <settings-form :breadcrumbs="breadcrumbs">
    <template #actions>
      <v-btn
        class="mr-2"
        color="success"
        :disabled="!isChanged"
        @click="save"
      >
        <v-icon>mdi-check</v-icon>
        {{ $t('button.save_changes') }}
      </v-btn>
    </template>
    <template #content>
      <!-- Grid -->
      <v-row justify="center">
        <v-col
          cols="12"
          md="8"
        >
          <v-row>
            <settings-button-block
              :title="$t('title.advanced_opening_hours')"
              :description="$t('label.description_advanced_hours')"
            >
              <v-btn
                :block="$vuetify.breakpoint.xsOnly"
                color="primary"
                outlined
                @click="$router.push('/settings/simple-hours')"
              >
                {{ $t('button.switch_to_simple_hours') }}
              </v-btn>
            </settings-button-block>
          </v-row>
        </v-col>
        <row-form-divider />
        <!-- Day Tab Selector -->
        <v-col
          cols="12"
          class="px-0"
        >
          <v-tabs
            v-model="dayIndex"
            show-arrows
            grow
            centered
          >
            <v-tab
              v-for="l in days"
              :key="`day_tab_${l.value}`"
            >
              {{ l.text }}
            </v-tab>
          </v-tabs>
        </v-col>
        <!-- /// -->

        <!-- Copy From Menu & Button-->
        <v-col
          cols="12"
          class="pa-0 ma-0 d-flex justify-center"
        >
          <v-menu offset-y>
            <template #activator="{ on }">
              <v-btn
                color="primary"
                v-on="on"
              >
                <v-icon small>
                  mdi-content-duplicate
                </v-icon> {{ $t('label.copy_from') }}
              </v-btn>
            </template>
            <v-list>
              <v-list-item
                v-for="(item, i) in days"
                :key="`menu_copy_from_${i}`"
                @click="copyFrom(i)"
              >
                <v-list-item-title>{{ item.text }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-col>
        <!-- /// -->
        <!-- TimeSlot Grid -->
        <hour-slots-grid
          :hour-slots="daySlots"
          :profiles="profiles"
          @profile-selected="profileSelected"
        />
        <!-- /// -->
      </v-row>
    </template>
  </settings-form>
</template>

<script lang="ts">
import Vue from 'vue';
import UnsavedGuard from '@/mixins/unsaved-guard';
import SettingsBreadcrumbs from '@/mixins/settings-breadcrumbs';
import HourSlotsGrid, { HourSlotMenuItemType } from '@/components/views/settings/HourSlotsGrid.vue';
import { performSaveAction } from '@/services/vue-utils';
import { isModelEqualToModel, cloneModel } from '@/model/model-utils';
import HourSlot from '@/model/HourSlot';
import HourSlotProfile, { profilesFromHours } from '@/model/HourSlotProfile';
import SettingsForm from '@/components/layouts/SettingsForm.vue';
import {
  DAY_TIME_SLOTS_COUNT, MORNING_TIME_SLOTS_COUNT, timeIndexFromSlot, weekdayNumberFromDateDay,
} from '@/services/time-utils';
import OpeningHour from '@/model/OpeningHour';
import SettingsButtonBlock from '@/components/forms/SettingsButtonBlock.vue';

export default Vue.extend({
  components: {
    SettingsForm, HourSlotsGrid, SettingsButtonBlock,
  },
  mixins: [UnsavedGuard, SettingsBreadcrumbs],
  data() {
    return {
      breadcrumbs: [],
      hours: [] as OpeningHour[],
      oldHours: [] as OpeningHour[],
      dayIndex: 0,
      unwatch: Function as (() => void),
    };
  },
  computed: {
    weekdayNumber(): number {
      return weekdayNumberFromDateDay(this.days[this.dayIndex].value);
    },
    isChanged(): boolean {
      return !isModelEqualToModel(this.hours, this.oldHours);
    },
    daySlots(): (OpeningHour | null)[] {
      const ohmap = new Map<number, OpeningHour>();
      this.hours.forEach((o) => { if (o.weekdayNumber === this.weekdayNumber) ohmap.set(o.timeIndex, o); });

      const slots: (OpeningHour|null)[] = [];
      for (let si = MORNING_TIME_SLOTS_COUNT; si < DAY_TIME_SLOTS_COUNT; si += 1) {
        const ti = timeIndexFromSlot(si);
        const oh = ohmap.get(ti) ?? null;
        slots.push(oh);
      }

      return slots;
    },
    days(): {value: number, text:string}[] {
      return this.$localized.longWeekdays();
    },
    profilesById(): Map<number, HourSlotProfile> {
      return profilesFromHours(this.hours);
    },
    profiles(): HourSlotProfile[] {
      return Array.from(this.profilesById.values());
    },
  },
  watch: {

  },
  created() {
    this.unwatch = this.$tstore.watch((state, getters) => getters.isLoaded, (newValue, oldValue) => {
      this.reset();
    });
  },
  beforeDestroy() {
    this.unwatch();
  },
  destroyed() {},
  mounted() {
    this.reset();
  },
  methods: {
    profileSelected(hourIndex: number, type: number, IDOrHourSlot: number | HourSlot) {
      const slotIndex = hourIndex + MORNING_TIME_SLOTS_COUNT;
      const timeIndex = timeIndexFromSlot(slotIndex);
      const index = this.hours.findIndex((o) => o.weekdayNumber === this.weekdayNumber && o.timeIndex === timeIndex);
      const hour = OpeningHour.fromWeekdayAndSlot(this.weekdayNumber, slotIndex);
      let profile: HourSlotProfile | undefined;

      switch (type) {
        case HourSlotMenuItemType.CLOSED:
        // closed - remove opening hour and return!
          if (index >= 0) this.hours.splice(index, 1);
          return;
        case HourSlotMenuItemType.OPEN:
        // just open - use empty hour
          break;
        case HourSlotMenuItemType.CUSTOM:
        // existing custom - fill and use hour from existing profile
          profile = this.profilesById.get(IDOrHourSlot as number);
          hour.setParams(profile ?? {});
          break;
        case HourSlotMenuItemType.CUSTOM_NEW:
        // new custom - fill and use hour from values
          hour.setParams(IDOrHourSlot as HourSlot);
          break;
        default:
          break;
      }

      // use hour
      if (index >= 0) this.hours[index] = hour;
      else this.hours.push(hour);
      this.hours = this.hours.sort((o1, o2) => o1.order - o2.order);
    },
    copyFrom(dayIndex: number) {
      const weekdayNumber = weekdayNumberFromDateDay(this.days[dayIndex].value);
      const newHours = this.hours.filter((o) => o.weekdayNumber !== this.weekdayNumber);
      const dayHours = this.hours.filter((o) => o.weekdayNumber === weekdayNumber);

      const newDayHours = dayHours.map((o) => {
        const no = cloneModel(o);
        no.weekdayNumber = this.weekdayNumber;
        return no;
      });
      newHours.push(...newDayHours);

      this.hours = newHours.sort((o1, o2) => o1.order - o2.order);
    },
    reset() {
      this.oldHours = this.$tstore.state.settings.openingHours;
      this.hours = this.oldHours.map((o) => cloneModel(o));
    },
    async save() {
      const entities = this.hours;

      const ok = await performSaveAction(
        undefined,
        async () => this.$tstore.dispatch('sendOpeningHours', { entities }),
      );

      if (ok) {
        this.reset();
      }
    },
  },
});
</script>

<style scoped>

  .openinghours-slot-button {
    color: white !important;
  }

</style>
